export const SUPPORTED_LOCALES = [
  "en",
  "ja",
  "ko",
  "zh-Hans",
  "zh-Hant",
  "de",
  "fr",
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const LOCALE_STORAGE_KEY = "geisha.webShop.locale";

type Messages = {
  officialShop: string;
  gameName: string;
  homeHeroLabel: string;
  homeWebStoreAria: string;
  homeWebStoreAlt: string;
  homeOfficialStoreLinks: string;
  unavailable: string;
  openFromAppTitle: string;
  openFromAppMessage: string;
  openingShop: string;
  totalGems: string;
  gems: string;
  buyOfferFor: string;
  loadShopFailed: string;
  checkoutStartFailed: string;
  loadingPacks: string;
  backToGame: string;
  noRecentOrder: string;
  paymentFailed: string;
  paymentProcessing: string;
  verifyOrderFailed: string;
  gemsDelivered: string;
  gemsAdded: string;
  balance: string;
  backToShop: string;
  orderPending: string;
  confirmingPayment: string;
  waitingPayment: string;
  checkoutNotCompleted: string;
  productTitles: Record<string, string>;
};

export const messages: Record<SupportedLocale, Messages> = {
  en: {
    officialShop: "Official Shop",
    gameName: "AmaZeus: Merge & Shoot",
    homeHeroLabel: "AmaZeus: Merge & Shoot",
    homeWebStoreAria: "Visit the web store",
    homeWebStoreAlt: "Web Store",
    homeOfficialStoreLinks: "Official store links",
    unavailable: "Unavailable",
    openFromAppTitle: "Open from the app",
    openFromAppMessage:
      "Please open this page from the AmaZeus app so we can connect your game account.",
    openingShop: "Opening shop",
    totalGems: "Total gems {count}",
    gems: "Gems",
    buyOfferFor: "Buy {name} for {price}",
    loadShopFailed: "Unable to load shop.",
    checkoutStartFailed: "Unable to start checkout.",
    loadingPacks: "Loading packs",
    backToGame: "Back to game",
    noRecentOrder: "No recent order found in this browser session.",
    paymentFailed: "Payment could not be completed for this order.",
    paymentProcessing: "Payment is still processing. Check your balance again later.",
    verifyOrderFailed: "Unable to verify order.",
    gemsDelivered: "Gems delivered",
    gemsAdded: "{count} gems were added to your wallet.",
    balance: "Balance: {count} gems",
    backToShop: "Back to shop",
    orderPending: "Order pending",
    confirmingPayment: "Confirming payment",
    waitingPayment: "Waiting for payment confirmation.",
    checkoutNotCompleted: "Checkout not completed",
    productTitles: {
      shop_gem_pack_1: "Some Gems",
      shop_gem_pack_2: "Pile of Gems",
      shop_gem_pack_3: "Heap of Gems",
      shop_gem_pack_4: "Bag of Gems",
      shop_gem_pack_5: "Bucket of Gems",
      shop_gem_pack_6: "Chest of Gems",
    },
  },
  ja: {
    officialShop: "公式ストア",
    gameName: "AmaZeus: Merge & Shoot",
    homeHeroLabel: "AmaZeus: Merge & Shoot",
    homeWebStoreAria: "ウェブストアを見る",
    homeWebStoreAlt: "ウェブストア",
    homeOfficialStoreLinks: "公式ストアリンク",
    unavailable: "購入不可",
    openFromAppTitle: "アプリから開いてください",
    openFromAppMessage:
      "ゲームアカウントを接続するため、AmaZeus アプリからこのページを開いてください。",
    openingShop: "ショップを開いています",
    totalGems: "ジェム合計 {count}",
    gems: "ジェム",
    buyOfferFor: "{name}を{price}で購入",
    loadShopFailed: "ショップを読み込めませんでした。",
    checkoutStartFailed: "購入手続きを開始できませんでした。",
    loadingPacks: "パックを読み込み中",
    backToGame: "ゲームに戻る",
    noRecentOrder: "このブラウザセッションに最近の注文がありません。",
    paymentFailed: "この注文の支払いを完了できませんでした。",
    paymentProcessing: "支払いを処理中です。後でもう一度残高を確認してください。",
    verifyOrderFailed: "注文を確認できませんでした。",
    gemsDelivered: "ジェムを受け取りました",
    gemsAdded: "{count} ジェムがウォレットに追加されました。",
    balance: "残高: {count} ジェム",
    backToShop: "ショップに戻る",
    orderPending: "注文確認中",
    confirmingPayment: "支払いを確認中",
    waitingPayment: "支払い確認を待っています。",
    checkoutNotCompleted: "購入手続きは完了していません",
    productTitles: {
      shop_gem_pack_1: "ジェム(ミニ)",
      shop_gem_pack_2: "ジェム(小)",
      shop_gem_pack_3: "ジェム(中)",
      shop_gem_pack_4: "ジェム(大)",
      shop_gem_pack_5: "ジェム(特大)",
      shop_gem_pack_6: "ジェム(超)",
    },
  },
  ko: {
    officialShop: "공식 상점",
    gameName: "AmaZeus: Merge & Shoot",
    homeHeroLabel: "AmaZeus: Merge & Shoot",
    homeWebStoreAria: "웹 스토어 방문",
    homeWebStoreAlt: "웹 스토어",
    homeOfficialStoreLinks: "공식 스토어 링크",
    unavailable: "구매 불가",
    openFromAppTitle: "앱에서 열어 주세요",
    openFromAppMessage:
      "게임 계정을 연결할 수 있도록 AmaZeus 앱에서 이 페이지를 열어 주세요.",
    openingShop: "상점을 여는 중",
    totalGems: "보석 합계 {count}",
    gems: "보석",
    buyOfferFor: "{name} {price}에 구매",
    loadShopFailed: "상점을 불러올 수 없습니다.",
    checkoutStartFailed: "결제를 시작할 수 없습니다.",
    loadingPacks: "팩 불러오는 중",
    backToGame: "게임으로 돌아가기",
    noRecentOrder: "이 브라우저 세션에서 최근 주문을 찾을 수 없습니다.",
    paymentFailed: "이 주문의 결제를 완료할 수 없습니다.",
    paymentProcessing: "결제 처리 중입니다. 나중에 잔액을 다시 확인해 주세요.",
    verifyOrderFailed: "주문을 확인할 수 없습니다.",
    gemsDelivered: "보석 지급 완료",
    gemsAdded: "보석 {count}개가 지갑에 추가되었습니다.",
    balance: "잔액: 보석 {count}개",
    backToShop: "상점으로 돌아가기",
    orderPending: "주문 대기 중",
    confirmingPayment: "결제 확인 중",
    waitingPayment: "결제 확인을 기다리는 중입니다.",
    checkoutNotCompleted: "결제가 완료되지 않았습니다",
    productTitles: {
      shop_gem_pack_1: "소량 보석",
      shop_gem_pack_2: "보석 더미",
      shop_gem_pack_3: "큰 더미 보석",
      shop_gem_pack_4: "보석 가방",
      shop_gem_pack_5: "보석 버킷",
      shop_gem_pack_6: "보석 상자",
    },
  },
  "zh-Hans": {
    officialShop: "官方商店",
    gameName: "AmaZeus: Merge & Shoot",
    homeHeroLabel: "AmaZeus: Merge & Shoot",
    homeWebStoreAria: "访问网页商店",
    homeWebStoreAlt: "网页商店",
    homeOfficialStoreLinks: "官方商店链接",
    unavailable: "暂不可用",
    openFromAppTitle: "请从 App 中打开",
    openFromAppMessage:
      "请从 AmaZeus App 中打开此页面，以便连接你的游戏账号。",
    openingShop: "正在打开商店",
    totalGems: "宝石总数 {count}",
    gems: "宝石",
    buyOfferFor: "以 {price} 购买{name}",
    loadShopFailed: "无法加载商店。",
    checkoutStartFailed: "无法开始结账。",
    loadingPacks: "正在加载礼包",
    backToGame: "返回游戏",
    noRecentOrder: "当前浏览器会话中没有最近订单。",
    paymentFailed: "此订单的支付未能完成。",
    paymentProcessing: "支付仍在处理中，请稍后再次查看余额。",
    verifyOrderFailed: "无法验证订单。",
    gemsDelivered: "宝石已到账",
    gemsAdded: "{count} 颗宝石已加入你的钱包。",
    balance: "余额：{count} 颗宝石",
    backToShop: "返回商店",
    orderPending: "订单处理中",
    confirmingPayment: "正在确认支付",
    waitingPayment: "正在等待支付确认。",
    checkoutNotCompleted: "结账未完成",
    productTitles: {
      shop_gem_pack_1: "少量宝石",
      shop_gem_pack_2: "一堆宝石",
      shop_gem_pack_3: "一大堆宝石",
      shop_gem_pack_4: "一袋宝石",
      shop_gem_pack_5: "一桶宝石",
      shop_gem_pack_6: "一箱宝石",
    },
  },
  "zh-Hant": {
    officialShop: "官方商店",
    gameName: "AmaZeus: Merge & Shoot",
    homeHeroLabel: "AmaZeus: Merge & Shoot",
    homeWebStoreAria: "前往網頁商店",
    homeWebStoreAlt: "網頁商店",
    homeOfficialStoreLinks: "官方商店連結",
    unavailable: "暫不可用",
    openFromAppTitle: "請從 App 中開啟",
    openFromAppMessage:
      "請從 AmaZeus App 中開啟此頁面，以便連接你的遊戲帳號。",
    openingShop: "正在開啟商店",
    totalGems: "寶石總數 {count}",
    gems: "寶石",
    buyOfferFor: "以 {price} 購買{name}",
    loadShopFailed: "無法載入商店。",
    checkoutStartFailed: "無法開始結帳。",
    loadingPacks: "正在載入禮包",
    backToGame: "返回遊戲",
    noRecentOrder: "目前瀏覽器工作階段中沒有最近訂單。",
    paymentFailed: "此訂單的付款未能完成。",
    paymentProcessing: "付款仍在處理中，請稍後再次查看餘額。",
    verifyOrderFailed: "無法驗證訂單。",
    gemsDelivered: "寶石已送達",
    gemsAdded: "{count} 顆寶石已加入你的錢包。",
    balance: "餘額：{count} 顆寶石",
    backToShop: "返回商店",
    orderPending: "訂單處理中",
    confirmingPayment: "正在確認付款",
    waitingPayment: "正在等待付款確認。",
    checkoutNotCompleted: "結帳未完成",
    productTitles: {
      shop_gem_pack_1: "少量寶石",
      shop_gem_pack_2: "一堆寶石",
      shop_gem_pack_3: "一大堆寶石",
      shop_gem_pack_4: "一袋寶石",
      shop_gem_pack_5: "一桶寶石",
      shop_gem_pack_6: "一箱寶石",
    },
  },
  de: {
    officialShop: "Offizieller Shop",
    gameName: "AmaZeus: Merge & Shoot",
    homeHeroLabel: "AmaZeus: Merge & Shoot",
    homeWebStoreAria: "Web-Shop besuchen",
    homeWebStoreAlt: "Web-Shop",
    homeOfficialStoreLinks: "Offizielle Store-Links",
    unavailable: "Nicht verfügbar",
    openFromAppTitle: "Aus der App öffnen",
    openFromAppMessage:
      "Bitte öffne diese Seite aus der AmaZeus App, damit wir dein Spielkonto verbinden können.",
    openingShop: "Shop wird geöffnet",
    totalGems: "Juwelen gesamt {count}",
    gems: "Juwelen",
    buyOfferFor: "{name} für {price} kaufen",
    loadShopFailed: "Shop konnte nicht geladen werden.",
    checkoutStartFailed: "Kauf konnte nicht gestartet werden.",
    loadingPacks: "Pakete werden geladen",
    backToGame: "Zurück zum Spiel",
    noRecentOrder: "In dieser Browsersitzung wurde keine aktuelle Bestellung gefunden.",
    paymentFailed: "Die Zahlung für diese Bestellung konnte nicht abgeschlossen werden.",
    paymentProcessing: "Die Zahlung wird noch verarbeitet. Prüfe dein Guthaben später erneut.",
    verifyOrderFailed: "Bestellung konnte nicht geprüft werden.",
    gemsDelivered: "Juwelen erhalten",
    gemsAdded: "{count} Juwelen wurden deinem Konto gutgeschrieben.",
    balance: "Guthaben: {count} Juwelen",
    backToShop: "Zurück zum Shop",
    orderPending: "Bestellung ausstehend",
    confirmingPayment: "Zahlung wird bestätigt",
    waitingPayment: "Warten auf Zahlungsbestätigung.",
    checkoutNotCompleted: "Kauf nicht abgeschlossen",
    productTitles: {
      shop_gem_pack_1: "Kleine Menge Edelsteine",
      shop_gem_pack_2: "Stapel Edelsteine",
      shop_gem_pack_3: "Großer Haufen Edelsteine",
      shop_gem_pack_4: "Beutel Edelsteine",
      shop_gem_pack_5: "Eimer Edelsteine",
      shop_gem_pack_6: "Truhe Edelsteine",
    },
  },
  fr: {
    officialShop: "Boutique officielle",
    gameName: "AmaZeus: Merge & Shoot",
    homeHeroLabel: "AmaZeus: Merge & Shoot",
    homeWebStoreAria: "Visiter la boutique web",
    homeWebStoreAlt: "Boutique web",
    homeOfficialStoreLinks: "Liens vers les boutiques officielles",
    unavailable: "Indisponible",
    openFromAppTitle: "Ouvrir depuis l'app",
    openFromAppMessage:
      "Ouvre cette page depuis l'app AmaZeus afin de connecter ton compte de jeu.",
    openingShop: "Ouverture de la boutique",
    totalGems: "Gemmes totales {count}",
    gems: "Gemmes",
    buyOfferFor: "Acheter {name} pour {price}",
    loadShopFailed: "Impossible de charger la boutique.",
    checkoutStartFailed: "Impossible de lancer le paiement.",
    loadingPacks: "Chargement des packs",
    backToGame: "Retour au jeu",
    noRecentOrder: "Aucune commande récente dans cette session de navigateur.",
    paymentFailed: "Le paiement de cette commande n'a pas pu être terminé.",
    paymentProcessing: "Le paiement est encore en cours. Vérifie ton solde plus tard.",
    verifyOrderFailed: "Impossible de vérifier la commande.",
    gemsDelivered: "Gemmes livrées",
    gemsAdded: "{count} gemmes ont été ajoutées à ton portefeuille.",
    balance: "Solde : {count} gemmes",
    backToShop: "Retour à la boutique",
    orderPending: "Commande en attente",
    confirmingPayment: "Confirmation du paiement",
    waitingPayment: "En attente de confirmation du paiement.",
    checkoutNotCompleted: "Paiement non terminé",
    productTitles: {
      shop_gem_pack_1: "Quelques gemmes",
      shop_gem_pack_2: "Tas de gemmes",
      shop_gem_pack_3: "Grand tas de gemmes",
      shop_gem_pack_4: "Sac de gemmes",
      shop_gem_pack_5: "Seau de gemmes",
      shop_gem_pack_6: "Coffre de gemmes",
    },
  },
};

export function normalizeSupportedLocale(value: string | null | undefined): SupportedLocale | null {
  const raw = value?.trim();
  if (!raw) return null;
  const normalized = raw.replace(/_/g, "-").toLowerCase();
  const base = normalized.split("-")[0];

  if (normalized === "jp" || base === "ja") return "ja";
  if (normalized === "kr" || base === "ko") return "ko";
  if (base === "de") return "de";
  if (base === "fr") return "fr";
  if (base === "en") return "en";
  if (normalized === "sc" || normalized === "zh-cn" || normalized === "zh-sg") {
    return "zh-Hans";
  }
  if (
    normalized === "tc" ||
    normalized === "zh-tw" ||
    normalized === "zh-hk" ||
    normalized === "zh-mo"
  ) {
    return "zh-Hant";
  }
  if (base === "zh") {
    return normalized.includes("hant") ? "zh-Hant" : "zh-Hans";
  }

  return null;
}

function safeReadStoredLocale(): SupportedLocale | null {
  try {
    return normalizeSupportedLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return null;
  }
}

function safeWriteStoredLocale(locale: SupportedLocale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage can be unavailable in private or embedded browser contexts.
  }
}

export function persistSupportedLocale(
  value: string | null | undefined,
): SupportedLocale | null {
  const locale = normalizeSupportedLocale(value);
  if (locale) safeWriteStoredLocale(locale);
  return locale;
}

export function resolveInitialLocale(): SupportedLocale {
  const params = new URLSearchParams(window.location.search);
  const queryLocale = persistSupportedLocale(params.get("lang"));
  if (queryLocale) return queryLocale;

  const storedLocale = safeReadStoredLocale();
  if (storedLocale) return storedLocale;

  const browserLocales =
    navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const browserLocale of browserLocales) {
    const locale = normalizeSupportedLocale(browserLocale);
    if (locale) return locale;
  }

  return "en";
}

export function formatMessage(
  template: string,
  values: Record<string, string | number> = {},
): string {
  return template.replace(/\{(\w+)\}/g, (_match, key: string) =>
    values[key] === undefined ? `{${key}}` : String(values[key]),
  );
}

export function formatNumber(locale: SupportedLocale, value: number): string {
  return new Intl.NumberFormat(locale).format(value);
}

export function getHeaderBannerImage(locale: SupportedLocale): string {
  switch (locale) {
    case "ja":
      return "webstore_header_banner_jp.webp";
    case "ko":
      return "webstore_header_banner_kr.webp";
    case "zh-Hans":
    case "zh-Hant":
      return "webstore_header_banner_zh.webp";
    case "de":
      return "webstore_header_banner_de.webp";
    case "fr":
      return "webstore_header_banner_fr.webp";
    default:
      return "webstore_header_banner.webp";
  }
}

export function getWebStoreButtonImage(locale: SupportedLocale): string {
  switch (locale) {
    case "ja":
      return "btn-webstore-jp.webp";
    case "ko":
      return "btn-webstore-kr.webp";
    case "zh-Hans":
      return "btn-webstore-sc.webp";
    case "zh-Hant":
      return "btn-webstore-tc.webp";
    case "de":
      return "btn-webstore-de.webp";
    case "fr":
      return "btn-webstore-fr.webp";
    default:
      return "btn-webstore-en.webp";
  }
}
